/**
 * Credit Decision API Client
 *
 * Calls the external credit decision API to determine which components to render
 * based on credit qualification scenarios. Uses single POST request with timeout
 * and provides comprehensive error handling.
 */

import type {
  CreditDecisionResponse,
  CreditDecisionRequest,
  PollingConfig,
  PollingState,
  PollingResult,
  PollingCallbacks,
  CreditDecisionApiError
} from './credit-decision-types'
import { getGclid, getGcid, cleanSessionId } from '../../utils/tracking-helpers'

/**
 * Credit Decision API Client Class
 *
 * Manages direct API calls to credit decision endpoint with
 * proper timeout handling and state management.
 */
export class CreditDecisionPoller {
  private config: PollingConfig
  private callbacks: PollingCallbacks
  private state: PollingState
  private abortController: AbortController | null = null
  private timeoutId: NodeJS.Timeout | null = null

  constructor(config: PollingConfig, callbacks: PollingCallbacks = {}) {
    this.config = config
    this.callbacks = callbacks
    this.state = this.createInitialState()
  }

  /**
   * Create initial state
   */
  private createInitialState(): PollingState {
    const now = Date.now()
    return {
      status: 'idle',
      attempt: 0,
      maxAttempts: 1, // Single request, not polling
      elapsedTime: 0,
      remainingTime: this.config.timeoutMs,
      aborted: false,
      startTime: now
    }
  }

  /**
   * Call credit decision API
   */
  async startPolling(): Promise<PollingResult> {
    if (this.state.status === 'polling') {
      throw new Error('API call is already in progress')
    }

    // Reset state for new request
    this.state = this.createInitialState()
    this.state.status = 'polling'
    this.state.startTime = Date.now()
    this.state.attempt = 1

    // Create abort controller for timeout management
    this.abortController = new AbortController()

    if (this.config.debugMode) {
      console.log('📞 Calling credit decision API', {
        sessionId: this.config.sessionId,
        endpoint: this.config.apiBaseUrl,
        timeoutMs: this.config.timeoutMs
      })
    }

    // Notify start
    this.callbacks.onStart?.(this.state)
    this.callbacks.onAttempt?.(this.getState(), 1)

    // Set timeout
    this.timeoutId = setTimeout(() => {
      this.handleTimeout()
    }, this.config.timeoutMs)

    try {
      const response = await this.makeApiRequest()

      if (response) {
        // Success - we got a decision
        this.state.status = 'success'
        const result = this.createSuccessResult(response)

        if (this.config.debugMode) {
          console.log('✅ Credit decision received:', response)
        }

        this.callbacks.onDecision?.(response, this.getState())
        this.callbacks.onComplete?.(result)
        return result
      } else {
        // No decision returned
        return this.createErrorResult('No credit decision returned', 'error')
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return this.createErrorResult('Request timed out', 'timeout')
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown API error'

      if (this.config.debugMode) {
        console.error('❌ Credit decision API error:', errorMessage)
      }

      this.callbacks.onError?.(errorMessage, this.getState())
      return this.createErrorResult(errorMessage, 'error')
    } finally {
      this.cleanup()
    }
  }

  /**
   * Stop API call (abort)
   */
  stopPolling(): void {
    if (this.state.status !== 'polling') {
      return
    }

    this.state.status = 'aborted'
    this.state.aborted = true
    this.abortController?.abort()

    if (this.config.debugMode) {
      console.log('🛑 Credit decision API call aborted')
    }

    this.callbacks.onAbort?.(this.state)
    this.cleanup()
  }

  /**
   * Get current state
   */
  getState(): PollingState {
    // Update elapsed and remaining time
    const now = Date.now()
    const elapsed = now - this.state.startTime
    const remaining = Math.max(0, this.config.timeoutMs - elapsed)

    return {
      ...this.state,
      elapsedTime: elapsed,
      remainingTime: remaining
    }
  }

  /**
   * Make API request to external credit decision endpoint
   */
  private async makeApiRequest(): Promise<CreditDecisionResponse | null> {
    // POST to external credit decision API
    const url = this.config.apiBaseUrl

    const headers = {
      'Content-Type': 'application/json',
      'x-auth': this.config.headers?.['x-auth'] || '',
      ...this.config.headers
    }

    // Build request body with tracking data
    // Use tracking data from config if available, otherwise extract from browser
    const requestBody: CreditDecisionRequest = {
      session_id: this.config.sessionId,
      gclid: this.config.trackingData?.gclid || getGclid(),
      gcid: this.config.trackingData?.gcid || getGcid(),
      external_transaction_id: this.config.trackingData?.external_transaction_id || cleanSessionId(this.config.sessionId),
      client_ip_address: this.config.trackingData?.client_ip_address
    }

    const body = JSON.stringify(requestBody)

    if (this.config.debugMode) {
      console.log('📤 Sending credit decision request:', {
        url,
        sessionId: this.config.sessionId,
        trackingData: {
          gclid: requestBody.gclid || '(not set)',
          gcid: requestBody.gcid || '(not set)',
          external_transaction_id: requestBody.external_transaction_id,
          client_ip_address: requestBody.client_ip_address || '(not set)'
        },
        headers: { 'x-auth': headers['x-auth']?.substring(0, 10) + '...' }
      })
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body,
      signal: this.abortController?.signal
    })

    if (!response.ok) {
      // Handle errors
      let errorDetails: CreditDecisionApiError
      try {
        errorDetails = await response.json()
      } catch {
        errorDetails = {
          code: 'HTTP_ERROR',
          message: `HTTP ${response.status}: ${response.statusText}`,
          timestamp: new Date().toISOString()
        }
      }

      throw new Error(`API Error: ${errorDetails.message}`)
    }

    let apiResponse = await response.json()

    // Handle array wrapper - API may return [{...}] instead of {...}
    if (Array.isArray(apiResponse) && apiResponse.length > 0) {
      if (this.config.debugMode) {
        console.log('📦 API response is an array, unwrapping first element')
      }
      apiResponse = apiResponse[0]
    }

    if (this.config.debugMode) {
      console.log('📥 Credit decision API response:', apiResponse)
    }

    // Extract scenario from response
    let scenario: number | undefined

    // Common patterns for scenario location in response
    if (typeof apiResponse.scenario === 'number') {
      scenario = apiResponse.scenario
    } else if (typeof apiResponse.scenarioCode === 'number') {
      scenario = apiResponse.scenarioCode
    } else if (typeof apiResponse.scenario_code === 'number') {
      scenario = apiResponse.scenario_code
    } else if (apiResponse.data?.scenario) {
      scenario = apiResponse.data.scenario
    } else if (apiResponse.result?.scenario) {
      scenario = apiResponse.result.scenario
    }

    if (scenario === undefined) {
      if (this.config.debugMode) {
        console.warn('⚠️ Could not extract scenario from API response:', apiResponse)
      }
      throw new Error('No scenario code found in API response')
    }

    // Validate scenario codes (1-10, 90-99)
    if (!this.isValidScenario(scenario)) {
      if (this.config.debugMode) {
        console.warn('⚠️ Received invalid scenario code:', scenario)
      }
      throw new Error(`Invalid scenario code: ${scenario}`)
    }

    // Extract DebtSettlementEstimate if present (handle both PascalCase and camelCase)
    let debtSettlementEstimate: import('./credit-decision-types').DebtSettlementEstimate | undefined

    const debtSettlementData = apiResponse.DebtSettlementEstimate || apiResponse.debtSettlementEstimate
    if (debtSettlementData) {
      debtSettlementEstimate = {
        DrPreQualDebtAmount: debtSettlementData.DrPreQualDebtAmount || debtSettlementData.drPreQualDebtAmount || 0,
        EstimatedPayment: debtSettlementData.EstimatedPayment || debtSettlementData.estimatedPayment || 0,
        HasErrors: debtSettlementData.HasErrors || debtSettlementData.hasErrors || false,
        ErrorMessage: debtSettlementData.ErrorMessage || debtSettlementData.errorMessage || null
      }

      if (this.config.debugMode) {
        console.log('📊 Debt Settlement Estimate:', debtSettlementEstimate)
      }
    }

    // Extract EvenOffers if present (handle both PascalCase and camelCase)
    let evenOffers: import('./credit-decision-types').EvenOffers | undefined

    const evenOffersData = apiResponse.EvenOffers || apiResponse.evenOffers
    if (evenOffersData) {
      evenOffers = {
        uuid: evenOffersData.uuid,
        leadUuid: evenOffersData.leadUuid,
        loanAmount: evenOffersData.loanAmount,
        loanOffers: evenOffersData.loanOffers || [],
        ErrorGettingOffers: evenOffersData.ErrorGettingOffers || evenOffersData.errorGettingOffers || false,
        ErrorMessage: evenOffersData.ErrorMessage || evenOffersData.errorMessage || null
      }

      if (this.config.debugMode) {
        console.log('🏦 Even Financial Loan Offers:', evenOffers)
      }
    }

    // Create response object
    const creditDecisionResponse: CreditDecisionResponse = {
      sessionId: this.config.sessionId,
      scenario: scenario,
      timestamp: apiResponse.timestamp || new Date().toISOString(),
      decision: apiResponse.decision,
      DebtSettlementEstimate: debtSettlementEstimate,
      EvenOffers: evenOffers,
      metadata: {
        processingTime: apiResponse.processingTime,
        confidence: apiResponse.confidence,
        factors: apiResponse.factors,
        externalResponse: apiResponse
      }
    }

    return creditDecisionResponse
  }

  /**
   * Validate scenario codes
   */
  private isValidScenario(scenario: number): boolean {
    return (scenario >= 1 && scenario <= 10) || (scenario >= 90 && scenario <= 99)
  }

  /**
   * Handle timeout
   */
  private handleTimeout(): void {
    if (this.state.status !== 'polling') {
      return
    }

    this.state.status = 'timeout'
    this.abortController?.abort()

    if (this.config.debugMode) {
      console.log('⏰ Credit decision API request timed out')
    }
  }

  /**
   * Create success result
   */
  private createSuccessResult(response: CreditDecisionResponse): PollingResult {
    return {
      success: true,
      response,
      state: this.getState(),
      totalTime: Date.now() - this.state.startTime,
      endReason: 'success'
    }
  }

  /**
   * Create error result
   */
  private createErrorResult(error: string, endReason: PollingResult['endReason']): PollingResult {
    return {
      success: false,
      error,
      state: this.getState(),
      totalTime: Date.now() - this.state.startTime,
      endReason
    }
  }

  /**
   * Cleanup resources
   */
  private cleanup(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }

    this.abortController = null
  }
}

/**
 * Convenience function to create and start polling
 */
export async function pollCreditDecision(
  config: PollingConfig,
  callbacks: PollingCallbacks = {}
): Promise<PollingResult> {
  const poller = new CreditDecisionPoller(config, callbacks)
  return await poller.startPolling()
}